import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck, Undo2, Users, PenLine } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import AtRiskLearnersPanel from './AtRiskLearnersPanel';
import GradeFinalAssignmentDialog from './GradeFinalAssignmentDialog';

const API = `${API_BASE}/api`;

const CATEGORY_STYLES = {
  course_completion: { label: 'Course', cls: 'bg-teal-500/15 text-teal-300' },
  lab: { label: 'Lab', cls: 'bg-indigo-500/15 text-indigo-300' },
  assignments: { label: 'Assignment', cls: 'bg-rose-500/15 text-rose-300' },
};

const WEIGHT_FIELDS = [
  { key: 'course_completion', label: 'Course completion', hint: 'Completion + quiz score (if the course has a quiz)' },
  { key: 'lab', label: 'Learning lab completion', hint: 'Best lab competency score' },
  { key: 'assignments', label: 'Assignments', hint: 'Admin-graded assignments & POE, incl. final' },
];

const CategoryWeights = ({ form, setForm, categories }) => {
  const total = WEIGHT_FIELDS.reduce((sum, f) => sum + (Number(form.category_weights[f.key]) || 0), 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-zinc-400">Category weighting (must total 100%)</p>
        <span className={`text-sm font-mono ${total === 100 ? 'text-green-400' : 'text-rose-400'}`} data-testid="rubric-weight-total">{total}% / 100%</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {WEIGHT_FIELDS.map((f) => {
          const cat = categories?.find((c) => c.key === f.key);
          return (
            <div key={f.key} className="rounded-lg border border-zinc-800 p-3">
              <Label className="text-zinc-300 text-sm">{f.label}</Label>
              <div className="relative mt-1">
                <Input type="number" min={0} max={100} value={form.category_weights[f.key]} onChange={(e) => setForm({ ...form, category_weights: { ...form.category_weights, [f.key]: Number(e.target.value) } })} className="bg-zinc-800 border-zinc-700 text-white pr-8" data-testid={`rubric-weight-${f.key}`} />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1.5">{f.hint}</p>
              {cat && <p className="text-[11px] text-zinc-400 mt-1" data-testid={`rubric-category-info-${f.key}`}>{cat.item_count} item{cat.item_count === 1 ? '' : 's'} · {Number(cat.effective_points).toFixed(1)} pts applied</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RubricItemsTable = ({ items, onGradeNow, pendingCount }) => (
  <div className="overflow-x-auto" data-testid="rubric-items-table">
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-zinc-500 border-b border-zinc-800">
          <th className="py-2 pr-3">Item</th>
          <th className="py-2 pr-3">Activity</th>
          <th className="py-2 pr-3">Type</th>
          <th className="py-2 text-right">Points</th>
        </tr>
      </thead>
      <tbody>
        {items.map((i) => {
          const s = CATEGORY_STYLES[i.category] || CATEGORY_STYLES.course_completion;
          return (
            <tr key={i.item_id} className="border-b border-zinc-800/60" data-testid={`rubric-item-${i.item_id}`}>
              <td className="py-2.5 pr-3 text-zinc-200">{i.label}</td>
              <td className="py-2.5 pr-3 text-zinc-400">
                {i.activity_title || <span className="text-amber-400">Not linked</span>}
                {i.period_title && <span className="block text-xs text-zinc-600">{i.period_title}</span>}
              </td>
              <td className="py-2.5 pr-3"><Badge className={`text-[10px] ${s.cls}`}>{s.label}</Badge></td>
              <td className="py-2.5 text-right font-mono text-white">
                <span className="inline-flex items-center gap-3">
                  {i.is_final && i.activity_id && (
                    <Button size="sm" onClick={onGradeNow} className="h-7 bg-rose-600 hover:bg-rose-700 text-white font-sans" data-testid="rubric-grade-now-btn">
                      <PenLine className="h-3.5 w-3.5 mr-1.5" /> Grade now{pendingCount ? ` (${pendingCount})` : ''}
                    </Button>
                  )}
                  {Number(i.points || 0).toFixed(1)}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const LearnerScores = ({ data }) => (
  <Card className="bg-zinc-900 border-zinc-800" data-testid="rubric-learner-scores">
    <CardHeader>
      <CardTitle className="text-white text-base flex items-center gap-2"><Users className="h-4 w-4 text-teal-400" /> Learner scores</CardTitle>
    </CardHeader>
    <CardContent>
      {!data?.learners?.length ? (
        <p className="text-sm text-zinc-500">No enrolled learners yet.</p>
      ) : (
        <div className="space-y-2">
          {data.learners.map((l) => (
            <div key={l.enrollment_id} className="flex items-center justify-between text-sm" data-testid={`rubric-learner-${l.enrollment_id}`}>
              <span className="text-zinc-300">{l.name || l.email}</span>
              <span className="flex items-center gap-2">
                <span className="font-mono text-white">{l.total_score} / 100</span>
                <Badge className={l.passed ? 'bg-green-500/15 text-green-400' : 'bg-zinc-700/40 text-zinc-300'}>{l.passed ? 'Pass' : 'Not yet'}</Badge>
              </span>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

export default function ProgrammeRubricTab({ programmeId, token, refreshKey, onAtRiskCount }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [rubric, setRubric] = useState(null);
  const [form, setForm] = useState(null);
  const [scores, setScores] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gradeOpen, setGradeOpen] = useState(false);

  const applyRubric = (r) => {
    setRubric(r);
    setForm({ pass_mark: r.pass_mark, final_assignment_activity_id: r.final_assignment_activity_id, category_weights: { ...r.category_weights } });
  };

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        axios.get(`${API}/programmes/${programmeId}/rubric`, { headers }),
        axios.get(`${API}/programmes/${programmeId}/rubric/scores`, { headers }),
      ]);
      applyRubric(r.data);
      setScores(s.data);
      onAtRiskCount?.(s.data.at_risk_count || 0);
    } catch (e) {
      toast.error('Failed to load assessment rubric');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeId, token]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const call = async (method, path, body) => {
    setBusy(true);
    try {
      const res = await axios[method](`${API}/programmes/${programmeId}/rubric${path}`, body, { headers });
      applyRubric(res.data);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || 'Rubric update failed');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    const total = WEIGHT_FIELDS.reduce((sum, f) => sum + (Number(form.category_weights[f.key]) || 0), 0);
    if (total !== 100) return toast.error(`Category weightings must add up to 100% (currently ${total}%)`);
    if (await call('put', '', form)) toast.success('Rubric settings saved — points rebalanced');
  };

  if (!rubric || !form) return <p className="text-zinc-500 text-sm py-8" data-testid="rubric-loading">Loading rubric...</p>;

  const approved = rubric.status === 'approved';
  return (
    <div className="space-y-6" data-testid="programme-rubric-tab">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-xs uppercase tracking-wide text-zinc-500">Total</p>
              <p className="text-2xl font-bold text-white" data-testid="rubric-total-points">{rubric.allocated_points} <span className="text-sm text-zinc-500">/ {rubric.total_points}</span></p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-zinc-500">Pass mark</p>
              <p className="text-2xl font-bold text-teal-400" data-testid="rubric-pass-mark">{rubric.pass_mark}</p>
            </div>
            <Badge data-testid="rubric-status-badge" className={approved ? 'bg-green-500/15 text-green-400' : 'bg-amber-500/15 text-amber-300'}>
              {approved ? 'Approved' : 'Pending approval'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} className="border-zinc-700 text-zinc-300" data-testid="rubric-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            {approved ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => call('post', '/revoke', {})} className="border-zinc-700 text-zinc-300" data-testid="rubric-revoke-btn">
                <Undo2 className="h-4 w-4 mr-2" /> Revoke approval
              </Button>
            ) : (
              <Button size="sm" disabled={busy} onClick={async () => { if (await call('post', '/approve', {})) toast.success('Rubric approved — learners can now see their scores'); }} className="bg-green-600 hover:bg-green-700 text-white" data-testid="rubric-approve-btn">
                <ShieldCheck className="h-4 w-4 mr-2" /> Approve rubric
              </Button>
            )}
          </div>
        </CardContent>
        {approved && rubric.approved_at && (
          <p className="px-5 pb-4 -mt-2 text-xs text-zinc-500 flex items-center gap-1.5" data-testid="rubric-approved-info">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> Approved by {rubric.approved_by_name || 'admin'} on {new Date(rubric.approved_at).toLocaleString()}. Points within each category rebalance automatically as activities change.
          </p>
        )}
      </Card>

      {rubric.warnings?.map((w) => (
        <div key={w} className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="rubric-warning">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {w}
        </div>
      ))}

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader><CardTitle className="text-white text-base">Rubric settings</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <CategoryWeights form={form} setForm={setForm} categories={rubric.categories} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-400">Pass mark (%)</Label>
              <Input type="number" min={0} max={100} value={form.pass_mark} onChange={(e) => setForm({ ...form, pass_mark: Number(e.target.value) })} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="rubric-pass-mark-input" />
            </div>
            <div>
              <Label className="text-zinc-400">Final integrated assignment</Label>
              <Select value={form.final_assignment_activity_id || ''} onValueChange={(v) => setForm({ ...form, final_assignment_activity_id: v })}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="rubric-final-activity-select">
                  <SelectValue placeholder="Auto: last assignment brief" />
                </SelectTrigger>
                <SelectContent>
                  {rubric.assignment_options.map((o) => (
                    <SelectItem key={o.activity_id} value={o.activity_id}>{o.title}{o.period_title ? ` — ${o.period_title}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button disabled={busy} onClick={saveSettings} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="rubric-save-settings-btn">Save rubric settings</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader><CardTitle className="text-white text-base">Points allocation</CardTitle></CardHeader>
        <CardContent>
          <RubricItemsTable
            items={rubric.items}
            onGradeNow={() => setGradeOpen(true)}
            pendingCount={(scores?.learners || []).filter((l) => l.final_submission && l.final_submission.status !== 'graded').length}
          />
        </CardContent>
      </Card>

      <AtRiskLearnersPanel learners={scores?.learners} passMark={rubric.pass_mark} programmeId={programmeId} token={token} onReminded={load} />

      <LearnerScores data={scores} />

      <GradeFinalAssignmentDialog
        open={gradeOpen}
        onOpenChange={setGradeOpen}
        learners={scores?.learners}
        programmeId={programmeId}
        token={token}
        assignmentTitle={rubric.items.find((i) => i.is_final)?.activity_title}
        onGraded={load}
      />
    </div>
  );
}
